import "../../styles/ui/native-animations.scss";

import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import cn from "classnames";

const useBrowserLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

// Keep closing content mounted until its CSS transition ends. Initially open
// content is expanded in SSR as well, so hydration cannot move the page.
const Collapse = ({open, children, className, duration = 0.8, delay = 0, useOpacity = true, space = "0px"}) => {
    const [present, setPresent] = useState(open);
    const [expanded, setExpanded] = useState(open);
    const elementRef = useRef(null);

    useBrowserLayoutEffect(() => {
        if (elementRef.current) elementRef.current.inert = !open;

        if (open) {
            setPresent(true);
            // Resolve the collapsed layout before starting an opening transition.
            elementRef.current?.getBoundingClientRect();
            const frame = requestAnimationFrame(() => setExpanded(true));
            return () => cancelAnimationFrame(frame);
        }

        setExpanded(false);
        if (!present) return;

        const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        const timer = window.setTimeout(() => setPresent(false), reducedMotion ? 0 : (duration + delay) * 1000);
        return () => window.clearTimeout(timer);
    }, [open, present, duration, delay]);

    if (!open && !present) return null;

    return (
        <div
            ref={elementRef}
            className={cn(className, "ui-collapse")}
            data-expanded={expanded}
            aria-hidden={!open}
            style={{
                "--collapse-duration": `${duration}s`,
                "--collapse-delay": `${delay}s`,
                "--collapse-hidden-opacity": useOpacity ? 0 : 1,
                "--collapse-space": space,
            }}
        >
            <div className="ui-collapse__content">{children}</div>
        </div>
    );
};

export default Collapse;
